"use client";

import { type ReactNode, useCallback, useMemo } from "react";

import {
  LocationProvider,
  useLocationContext,
  type HeroLocation,
} from "./LocationContext";
import { SearchProvider, useSearchContext } from "./SearchContext";
import { useFilterState } from "@/hooks/useFilterState";
import { ResultsProvider, useResultsContext } from "./ResultsContext";
import {
  MapInteractionProvider,
  useMapInteractionContext,
} from "./MapInteractionContext";
import { UIProvider, useUIContext } from "./UIContext";
import { buildHeroMapContextValue } from "./hero-map-context-value";

export interface HeroMapInitialSearch {
  location?: HeroLocation;
  quantity?: number;
  radiusMiles?: number;
}

export function HeroMapProvider({
  children,
  initialSearch,
}: {
  children: ReactNode;
  initialSearch?: HeroMapInitialSearch;
}) {
  return (
    <LocationProvider initialLocation={initialSearch?.location ?? null}>
      <SearchProvider
        initialQuantity={initialSearch?.quantity}
        initialRadiusMiles={initialSearch?.radiusMiles}
      >
        <ResultsProvider>
          <MapInteractionProvider>
            <UIProvider>{children}</UIProvider>
          </MapInteractionProvider>
        </ResultsProvider>
      </SearchProvider>
    </LocationProvider>
  );
}

export function useHeroMapContext() {
  const location = useLocationContext();
  const search = useSearchContext();
  const filter = useFilterState();
  const results = useResultsContext();
  const mapInteraction = useMapInteractionContext();
  const ui = useUIContext();

  const clearSearch = useCallback(() => {
    location.clearLocation();
    search.resetSearch();
    filter.resetFilters();
    results.setProducts([]);
    results.setMetadata(null);
    results.setIsLoading(false);
    results.setError(null);
    mapInteraction.setHoveredProductId(null);
    mapInteraction.setSelectedProductId(null);
    mapInteraction.setViewportCenter(null);
    mapInteraction.setOptimizedZoom(null);
    ui.setFlowStage("search");
    ui.setFiltersExpanded(false);
    ui.setListingPreview(null);
    ui.setViewportLocationName(null);
  }, [location, search, filter, results, mapInteraction, ui]);

  return useMemo(
    () =>
      buildHeroMapContextValue(
        location,
        search,
        filter,
        results,
        mapInteraction,
        ui,
        clearSearch,
      ),
    [location, search, filter, results, mapInteraction, ui, clearSearch],
  );
}
